// Copyright 2019 Sorama B.V.

import { autoinject, bindable, computedFrom, TaskQueue } from "aurelia-framework";
import { BindingSignaler } from "aurelia-templating-resources";
import { IPlotParent } from "controls/plot/IPlotParent";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotPartBase } from "controls/plot/PlotPartBase";
import { IExportable } from "libraries/Export/IExportable";
import { SVGNode } from "libraries/SVG/SVGNode";
import { Pressure } from "units/Pressure";
import { dBUnit, Unit } from "units/Unit";

@autoinject
export class ExtremaAxisPart extends PlotPartBase implements IExportable {
    //#region HTMLElements
    private linearAxisSvgElement: SVGGElement;
    //#endregion

    private bindingSignaler: BindingSignaler;
    private taskQueue: TaskQueue;

    //#region Bindables
    @bindable public extremalabelscolor = "white";
    @bindable public extremalabelsfontsize = 12;
    @bindable public axislabelscolor = "white";
    @bindable public axislabelsfontsize = 12;
    @bindable public yaxisunit: Unit;
    @bindable public xaxisunit: Unit;

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public showextrema = "x";

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public showlabels = "x";

    @bindable()
    public borderthickness = 2;

    @bindable
    public significantdigits = 3;

    //#endregion

    //#region Fields
    private useXBaseUnit = false;
    private useYBaseUnit = false;

    private _xAxisUnit: Unit;
    private _yAxisUnit: Unit;

    public viewBox = "0 0 1 1";

    public xMinimumLabel: string;
    public xMaximumLabel: string;
    public xUnitLabel: string;

    public yMinimumLabel: string;
    public yMaximumLabel: string;
    public yUnitLabel: string;

    public nonScientificDbDigits = 1;

    public pressureUnit: Unit = Pressure.pascal;

    @computedFrom("actualSize.height")
    public get extremaBackgroundHeight(): number {
        if (!this.actualSize) return 25;

        return Math.min(25, this.actualSize.height - 10);
    }

    //#endregion

    //#region Constructor and Initialisation

    constructor(bindingSignaler: BindingSignaler, taskQueue: TaskQueue) {
        super(0);

        this.bindingSignaler = bindingSignaler;
        this.taskQueue = taskQueue;
    }

    public bind(context: IPlotParent): void {
        super.bind(context);

        // Make sure all bound values are correct, aurelia fails to call the change callbacks the first times <_<

        this.onShowExtremaChanged(this.showextrema, "x");
        this.onShowLabelsChanged(this.showlabels, "x");

        this.useXBaseUnit = this.xaxisunit ? false : true;
        this.useYBaseUnit = this.yaxisunit ? false : true;

        this._xAxisUnit = this.xaxisunit;
        this._yAxisUnit = this.yaxisunit;
    }
    //#endregion

    //#region Change Handlers
    public onShowExtremaChanged(newValue: string, oldValue: string): void {
        if (!this.isValidXYSelector(newValue)) {
            console.error("Invalid XY selector");
            this.showlabels = oldValue;
            return;
        }

        this.onRedrawPropertyChanged();
    }

    public onShowLabelsChanged(newValue: string, oldValue: string): void {
        if (!this.isValidXYSelector(newValue)) {
            console.error("Invalid XY selector");
            this.showlabels = oldValue;
            return;
        }

        this.onRedrawPropertyChanged();
    }

    public xaxisunitChanged(newValue: Unit): void {
        this.useXBaseUnit = newValue ? false : true;
        if (newValue) {
            this._xAxisUnit = newValue;
        }
        this.onRedrawPropertyChanged();
    }

    public yaxisunitChanged(newValue: Unit): void {
        this.useYBaseUnit = newValue ? false : true;
        if (newValue) {
            this._yAxisUnit = newValue;
        }
        this.onRedrawPropertyChanged();
    }

    private onRedrawPropertyChanged(): void {
        this.plotParent.requestMeasure(true);
    }

    public onMeasureMargin(): Margin {
        return new Margin(
            this.borderthickness / 2,
            this.borderthickness / 2,
            this.borderthickness / 2,
            this.borderthickness / 2
        );
    }

    public onSize(size: Size, margin: Margin): void {
        // Wait before setting the size until we get a valid size
        if (size.width - margin.left - margin.right <= 0 || size.height - margin.top - margin.bottom <= 0) return;

        // Check for actual change
        if (
            this.size &&
            this.margin &&
            Math.abs(this.size.width - size.width) < 1e-5 &&
            Math.abs(this.size.height - size.height) < 1e-5 &&
            Math.abs(this.margin.left - margin.left) < 1e-5 &&
            Math.abs(this.margin.top - margin.top) < 1e-5 &&
            Math.abs(this.margin.right - margin.right) < 1e-5 &&
            Math.abs(this.margin.bottom - margin.bottom) < 1e-5
        )
            return; // Ignore update

        this.size = size;
        this.margin = margin;
        this.actualSize = new Size(size.width - margin.left - margin.right, size.height - margin.top - margin.bottom);

        this.viewBox = -margin.left + " " + -margin.top + " " + this.size.width + " " + this.size.height;

        this.updateControl();
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>): void {
        // Check for actual change
        if (
            this.viewPort &&
            this.viewPortBounds &&
            Math.abs(this.viewPort.x - viewPort.x) < 1e-5 &&
            Math.abs(this.viewPort.y - viewPort.y) < 1e-5 &&
            Math.abs(this.viewPort.width - viewPort.width) < 1e-5 &&
            Math.abs(this.viewPort.height - viewPort.height) < 1e-5 &&
            Math.abs(this.viewPortBounds.x - viewPortBounds.x) < 1e-5 &&
            Math.abs(this.viewPortBounds.y - viewPortBounds.y) < 1e-5 &&
            Math.abs(this.viewPortBounds.width - viewPortBounds.width) < 1e-5 &&
            Math.abs(this.viewPortBounds.height - viewPortBounds.height) < 1e-5 &&
            (!this.useXBaseUnit || (!this.useXBaseUnit && units.x == this._xAxisUnit)) &&
            (!this.useYBaseUnit || (!this.useYBaseUnit && units.y == this._yAxisUnit))
        )
            return; // Ignore update

        super.onViewPortComputed(viewPort, viewPortBounds, units);

        const prevX = this._xAxisUnit;
        const prevY = this._yAxisUnit;

        // Set the correct units
        if (this.useXBaseUnit) {
            this._xAxisUnit = units.x;
        }

        if (this.useYBaseUnit) {
            this._yAxisUnit = units.y;
        }

        if (
            ((!prevX || !prevX.unit) && this._xAxisUnit && this._xAxisUnit.unit) ||
            ((!prevY || !prevY.unit) && this._yAxisUnit && this._yAxisUnit.unit)
        ) {
            this.plotParent.requestMeasure(true);
        }
        this.updateControl();
    }

    //#endregion

    //#region Update and Draw Functions

    private updateControl(): void {
        if (
            !this.viewPort ||
            this.viewPort.width === 0 ||
            this.viewPort.height === 0 ||
            !this.actualSize ||
            !this.margin ||
            !this._xAxisUnit ||
            !this._yAxisUnit ||
            !this.sourceUnits ||
            !Number.isFinite(this.viewPort.width) ||
            !Number.isFinite(this.viewPort.height) ||
            !Number.isFinite(this.viewPort.x) ||
            !Number.isFinite(this.viewPort.y)
        )
            return;

        // Determine extrema texts
        if (this.xIsSelected(this.showextrema)) {
            //db units are better displayed without scientific notation
            const isScientific = !(this._xAxisUnit instanceof dBUnit);
            this.xMinimumLabel = this.valueToString(this.viewPort.x, isScientific);
            this.xMaximumLabel = this.valueToString(this.viewPort.x + this.viewPort.width, isScientific);
        }

        // Determine label texts
        if (this.xIsSelected(this.showlabels) && this._xAxisUnit.unit !== "") {
            this.xUnitLabel = this._xAxisUnit.toUnitType() + " [" + this._xAxisUnit.unit + "]";
        }

        this.taskQueue.queueTask(() => {
            this.bindingSignaler.signal("refresh-extrema-part");
        });
    }

    //#endregion

    //#region IExportable

    public exportToSvg(): Promise<SVGNode> {
        if (!this.linearAxisSvgElement) return undefined;

        const svgs = SVGNode.getSvgsInElement(this.linearAxisSvgElement);
        if (svgs.length != 1) return undefined; // Only 1 expected..

        return Promise.resolve(svgs[0]);
    }

    //#endregion

    //#region Helpers

    public computeBackgroundWidth(textElement: SVGTextElement): number {
        if (!textElement) {
            return 50;
        }

        return textElement.getComputedTextLength() + 10;
    }

    private valueToString(value: number, isScientific = true): string {
        let displayString;

        if (isScientific) {
            displayString = value.toLocaleString(undefined, {
                maximumSignificantDigits: this.significantdigits,
                useGrouping: false,
            });

            if (displayString.length > this.significantdigits + 1) {
                // Use scientific notation
                displayString = value.toExponential(this.significantdigits - 1);
            }
        } else {
            displayString = value.toFixed(this.nonScientificDbDigits);
        }

        return displayString;
    }

    private isValidXYSelector(value: string): boolean {
        if (value === null || value === undefined || typeof value !== "string") return false;

        const vv = value.toLowerCase();

        if (vv === "" || vv === "x" || vv === "y" || vv === "xy" || vv === "yx") return true;

        return false;
    }

    private xIsSelected(value: string): boolean {
        return value.indexOf("x") != -1;
    }

    //#endregion
}
